import React, { useMemo } from "react";
import {
  Button,
  Collapse,
  ConfigProvider,
  Empty,
  List,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { EyeOutlined, FileTextOutlined } from "@ant-design/icons";
import { buildFreightDocumentsByStoppingPoint } from "../../../util/freight/freight-document-utils";
import theme from "../../../theme";

const { Text } = Typography;

/** Acima do Drawer/AppBar do MUI (1200/1201). */
const ANTD_MODAL_Z_INDEX = 1400;

const antdModalTheme = {
  token: {
    colorPrimary: theme.colors.primary,
    colorText: theme.colors.textPrimary,
    colorTextSecondary: theme.colors.textSecondary,
    colorBorder: theme.colors.primaryBorder,
    borderRadius: theme.radius.small,
    fontFamily: theme.typography.fontFamily,
  },
};

function openDocument(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function FreightDocumentsModal({
  open,
  onClose,
  loading = false,
  freightData,
  freightLabel,
}) {
  const sections = useMemo(
    () => buildFreightDocumentsByStoppingPoint(freightData || {}),
    [freightData],
  );

  const defaultActiveKeys = useMemo(
    () => sections.map((section) => section.id),
    [sections],
  );

  const modalTitle = freightLabel
    ? `Documentos da fatura — ${freightLabel}`
    : "Documentos da fatura";

  return (
    <ConfigProvider theme={antdModalTheme}>
      <Modal
        open={open}
        zIndex={ANTD_MODAL_Z_INDEX}
        onCancel={onClose}
        title={modalTitle}
        footer={[
          <Button key="close" type="primary" onClick={onClose}>
            Fechar
          </Button>,
        ]}
        width={760}
        destroyOnClose
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            paddingTop: 8,
          },
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <Spin size="large" tip="Carregando documentos..." />
          </div>
        ) : sections.length === 0 ? (
          <Empty description="Nenhum ponto de parada encontrado para este frete." />
        ) : (
          <Collapse
            bordered={false}
            defaultActiveKey={defaultActiveKeys}
            items={sections.map((section) => ({
              key: section.id,
              label: (
                <Space size={8} wrap>
                  <Text strong>{section.title}</Text>
                  <Tag color={section.documents.length > 0 ? "blue" : "default"}>
                    {section.documents.length}{" "}
                    {section.documents.length === 1 ? "arquivo" : "arquivos"}
                  </Tag>
                </Space>
              ),
              children:
                section.documents.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Não há arquivos cadastrados neste ponto de parada."
                  />
                ) : (
                  <List
                    itemLayout="horizontal"
                    dataSource={section.documents}
                    renderItem={(documentItem) => (
                      <List.Item
                        actions={[
                          <Tooltip key="view" title="Visualizar documento">
                            <Button
                              type="link"
                              size="small"
                              icon={<EyeOutlined />}
                              disabled={!documentItem.canView}
                              onClick={() => openDocument(documentItem.url)}
                            >
                              Visualizar
                            </Button>
                          </Tooltip>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <FileTextOutlined
                              style={{
                                fontSize: 18,
                                color: theme.colors.primary,
                              }}
                            />
                          }
                          title={documentItem.label}
                          description={documentItem.name}
                        />
                      </List.Item>
                    )}
                  />
                ),
            }))}
          />
        )}
      </Modal>
    </ConfigProvider>
  );
}

export default FreightDocumentsModal;
